/*
  Automatic "buy 2, get 1 free" cart lines for the Perfumes and Reed Diffusers offers.

  The two Shopify automatic discounts ("Buy 2 Perfumes Get 1 Free" and
  "Buy 2 Get 1 FREE (Reed Diffusers)") make the free item cost 0. This script only makes
  sure the free item is a real line item on the order, so it shows on the packing slip.

  Rules, applied separately to each category (variant -> category comes from #FreeGiftData,
  built in Liquid from the two collections):
    free items = floor(items the customer chose in the category / 2)
  Quantities count, not lines. Free lines are tagged with the line item property
  "Free gift: Yes" (plus a hidden slot number so two free lines of the same product stay
  as two separate lines).

  Safety: one change at a time (a queue), debounced, cart re-read after every change,
  a hard cap on passes per trigger, and every failure is swallowed quietly.
*/
(function () {
  'use strict';

  var dataEl = document.getElementById('FreeGiftData');
  if (!dataEl) { return; }

  var DATA;
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var FREE_PROP = 'Free gift';
  var FREE_VALUE = 'Yes';
  var SLOT_PROP = '_free_gift_slot';
  var LAST_KEY = 'aimaFreeGiftLast';
  var SNAP_KEY = 'aimaFreeGiftSnapshot';
  var MAX_PASSES = 5;
  var DEBOUNCE_MS = 400;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var URLS = {
    cart: root + 'cart.js',
    add: root + 'cart/add.js',
    change: root + 'cart/change.js'
  };

  var categories = Object.keys(DATA.categories || {});
  var variantCategory = {};
  var variantInfo = {};
  categories.forEach(function (cat) {
    DATA.categories[cat].variants.forEach(function (v) {
      variantCategory[v.id] = cat;
      variantInfo[v.id] = v;
    });
  });

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!origFetch) { return; }

  /* ------------------------------------------------------------------ helpers */

  function store(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked */ }
  }

  function load(key) {
    try { return JSON.parse(window.localStorage.getItem(key)) || null; } catch (e) { return null; }
  }

  function isFree(item) {
    return !!(item.properties && item.properties[FREE_PROP] === FREE_VALUE);
  }

  function slotOf(item) {
    var n = parseInt(item.properties && item.properties[SLOT_PROP], 10);
    return isNaN(n) ? 0 : n;
  }

  function getCart() {
    return origFetch(URLS.cart + '?t=' + Date.now(), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function post(url, body) {
    return origFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok || (json && json.status && json.status >= 400)) {
          var err = new Error('cart request failed');
          err.status = r.status;
          throw err;
        }
        return json;
      });
    });
  }

  function isAvailable(variantId) {
    return !!(variantInfo[variantId] && variantInfo[variantId].available);
  }

  function availableVariants(cat) {
    return DATA.categories[cat].variants.filter(function (v) { return v.available; });
  }

  /* ----------------------------------------------------- deciding what to change */

  /*
    Works out, from the cart as it is right now, which free lines to add or remove.
    Also remembers the last fragrance the customer added in each category so the free
    line can default to it.
  */
  function computePlan(cart) {
    var paid = {};
    var freeLines = {};
    var lastPaidInCart = {};
    categories.forEach(function (cat) { paid[cat] = 0; freeLines[cat] = []; });

    var lastChosen = load(LAST_KEY) || {};
    var snapshot = load(SNAP_KEY);
    var newSnapshot = {};

    cart.items.forEach(function (item) {
      var cat = variantCategory[item.variant_id];
      if (!cat) { return; }
      if (isFree(item)) {
        freeLines[cat].push(item);
      } else {
        paid[cat] += item.quantity;
        // /cart.js lists the newest line first, so the first paid line seen is the latest one added
        if (!lastPaidInCart[cat]) { lastPaidInCart[cat] = item.variant_id; }
        newSnapshot[item.variant_id] = (newSnapshot[item.variant_id] || 0) + item.quantity;
        // a paid line whose quantity went up is "the last thing the customer added"
        if (snapshot && item.quantity > (snapshot[item.variant_id] || 0)) {
          lastChosen[cat] = item.variant_id;
        }
      }
    });

    categories.forEach(function (cat) {
      if (!lastChosen[cat] || !newSnapshot[lastChosen[cat]]) {
        // first visit, or that item has left the cart: fall back to the newest paid line
        if (lastPaidInCart[cat]) { lastChosen[cat] = lastPaidInCart[cat]; } else { delete lastChosen[cat]; }
      }
    });
    store(LAST_KEY, lastChosen);
    store(SNAP_KEY, newSnapshot);

    var actions = [];

    categories.forEach(function (cat) {
      var required = Math.floor(paid[cat] / 2);
      var lines = freeLines[cat].slice().sort(function (a, b) { return slotOf(a) - slotOf(b); });

      // a free line must always be exactly 1
      lines.forEach(function (line) {
        if (line.quantity !== 1) { actions.push({ type: 'quantity', key: line.key, quantity: 1 }); }
      });

      if (lines.length > required) {
        // too many free lines (customer dropped below the threshold): remove the highest slots first
        lines.slice(required).reverse().forEach(function (line) {
          actions.push({ type: 'remove', key: line.key });
        });
      } else if (lines.length < required) {
        var usedSlots = lines.map(slotOf);
        for (var i = lines.length; i < required; i++) {
          var slot = 1;
          while (usedSlots.indexOf(slot) !== -1) { slot++; }
          usedSlots.push(slot);
          actions.push({ type: 'add', category: cat, slot: slot, preferred: lastChosen[cat] });
        }
      }
    });

    return actions;
  }

  /* ----------------------------------------------------------- changing the cart */

  function addFree(cat, slot, preferred, exclude) {
    // preferred fragrance first, then every other available one in the category
    var candidates = [];
    if (preferred && isAvailable(preferred)) { candidates.push(preferred); }
    availableVariants(cat).forEach(function (v) {
      if (candidates.indexOf(v.id) === -1) { candidates.push(v.id); }
    });
    if (exclude) { candidates = candidates.filter(function (id) { return id !== exclude; }); }

    function attempt(index) {
      if (index >= candidates.length) { return Promise.reject(new Error('nothing available')); }
      var id = candidates[index];
      var properties = {};
      properties[FREE_PROP] = FREE_VALUE;
      properties[SLOT_PROP] = String(slot);
      return post(URLS.add, { items: [{ id: id, quantity: 1, properties: properties }] })
        .catch(function (err) {
          if (err && err.status === 422) {
            // sold out since the page loaded: never offer it again, try the next one
            if (variantInfo[id]) { variantInfo[id].available = false; }
            return attempt(index + 1);
          }
          throw err;
        });
    }
    return attempt(0);
  }

  function runAction(action) {
    if (action.type === 'add') { return addFree(action.category, action.slot, action.preferred); }
    if (action.type === 'remove') { return post(URLS.change, { id: action.key, quantity: 0 }); }
    if (action.type === 'quantity') { return post(URLS.change, { id: action.key, quantity: action.quantity }); }
    return Promise.resolve();
  }

  function runSequentially(actions) {
    return actions.reduce(function (chain, action) {
      return chain.then(function () { return runAction(action); });
    }, Promise.resolve());
  }

  function refreshCartUI() {
    // the theme listens for this and rebuilds both the drawer and the cart page
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /* ------------------------------------------------- queue, debounce, reconcile */

  var chain = Promise.resolve();
  var timer = null;
  var reconcileQueued = false;

  function queue(task) {
    chain = chain.then(task).catch(function () { /* fail quietly */ });
    return chain;
  }

  function reconcileOnce() {
    var changed = false;

    function pass(n) {
      if (n >= MAX_PASSES) { return Promise.resolve(); }
      return getCart().then(function (cart) {
        var plan = computePlan(cart);
        if (!plan.length) { return; }
        return runSequentially(plan).then(function () {
          changed = true;
          return pass(n + 1);
        });
      });
    }

    return pass(0).catch(function () { /* fail quietly */ }).then(function () {
      if (changed) { refreshCartUI(); }
    });
  }

  function schedule(delay) {
    window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      if (reconcileQueued) { return; }
      reconcileQueued = true;
      queue(function () {
        reconcileQueued = false;
        return reconcileOnce();
      });
    }, delay === undefined ? DEBOUNCE_MS : delay);
  }

  /* ---------------------------------------------- the free line's fragrance dropdown */

  document.addEventListener('change', function (evt) {
    var select = evt.target && evt.target.closest ? evt.target.closest('[data-free-gift-select]') : null;
    if (!select) { return; }

    var newId = parseInt(select.value, 10);
    var oldKey = select.getAttribute('data-key');
    var slot = parseInt(select.getAttribute('data-slot'), 10) || 1;
    var cat = select.getAttribute('data-category');
    if (!newId || !oldKey || !cat) { return; }

    select.disabled = true;

    queue(function () {
      var properties = {};
      properties[FREE_PROP] = FREE_VALUE;
      properties[SLOT_PROP] = String(slot);
      // add the new fragrance first, and only then remove the old one, so a failure never leaves the customer without their free item
      return post(URLS.add, { items: [{ id: newId, quantity: 1, properties: properties }] })
        .then(function () { return post(URLS.change, { id: oldKey, quantity: 0 }); })
        .catch(function (err) {
          if (err && err.status === 422 && variantInfo[newId]) { variantInfo[newId].available = false; }
        })
        .then(reconcileOnce)
        .then(refreshCartUI);
    });
  });

  /* ------------------------------------------- notice cart changes made elsewhere */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // theme / product forms using fetch
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = origFetch.apply(this, arguments);
    if (CART_WRITE.test(url)) {
      promise.then(function () { schedule(); }, function () { /* ignore */ });
    }
    return promise;
  };

  // anything using XMLHttpRequest
  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__freeGiftUrl = typeof url === 'string' ? url : '';
      return open.apply(this, arguments);
    };
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (CART_WRITE.test(this.__freeGiftUrl || '')) {
        this.addEventListener('loadend', function () { schedule(); });
      }
      return send.apply(this, arguments);
    };
  }

  document.addEventListener('ajaxProduct:added', function () { schedule(); });
  document.addEventListener('cart:updated', function () { schedule(); });

  // page load, coming back via the back button, and returning to the tab (e.g. after checkout)
  schedule(300);
  window.addEventListener('pageshow', function (evt) { if (evt.persisted) { schedule(300); } });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) { schedule(600); } });
})();
